import type { TransactionType } from '@/features/payouts/transaction-type'
import type { TransactionSourceType } from '@/features/payouts/transaction-source-type'
import type { TransactionFlag } from '@/features/payouts/transaction-flag'
import type { UserTransactionReason } from '@/features/payouts/user-transaction-reason'
import {
  mapTransactionFailureReasonResourceToTransactionFailureReason,
  mapTransactionFlagResourceToTransactionFlag,
  mapTransactionFlagToResource,
  mapTransactionSourceTypeResourceToTransactionSourceType,
  mapTransactionSourceTypeToResource,
  mapTransactionTypeResourceToTransactionType,
  mapTransactionTypeToResource,
  mapUserTransactionReasonResourceToUserTransactionReason,
  mapUserTransactionReasonToResource,
} from '@/features/payouts/transaction-mappers'
import { type Member } from '@/features/iam/members/Member'
import { type User } from '@/features/iam/users/User'
import type {
  ListWorkspacesFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
  SearchMembersFn,
} from '@/features/iam'
import { collection } from '@/infra/collection'
import { noop } from 'lodash-es'
import { type PayoutsAdminApi } from '@/features/payouts/api/admin-api'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import {
  type ApprovedTransactionResource,
  type DraftPayoutResource,
  type PendingTransactionResource,
} from '@/features/payouts/api/resources/drafting'
import {
  PendingTransaction,
  type PendingTransactionProps,
} from './PendingTransaction'
import { Subject } from 'rxjs'
import { NotFound } from 'fejl'
import { DraftPayout, type DraftPayoutProps } from './DraftPayout'
import {
  type ApprovedTransaction,
  type ApprovedTransactionProps,
} from './ApprovedTransaction'
import type { GetCurrentUserFn } from '@/features/iam'
import type { Workspace } from '@/features/iam/workspaces/Workspace'
import type { TransactionDateRange } from '@/features/payouts/transaction-date-range'
import { maybeParseDate } from '@/utils/date-util'

/**
 * Manages logic, data and API interactions for reviewing payouts.
 */
export class PayoutReviewStore {
  /**
   * Used to notify of added pending transactions.
   * @private
   */
  private readonly _pendingTransactionAdded = new Subject<PendingTransaction>()

  /**
   * Used to notify of removed pending transactions.
   * @private
   */
  private readonly _pendingTransactionRemoved =
    new Subject<PendingTransaction>()

  private _allPlatformWorkspaces: Promise<CursorPaged<Workspace>> | null = null

  /**
   * Emits a value whenever a pending transaction is added.
   */
  readonly pendingTransactionAdded$ =
    this._pendingTransactionAdded.asObservable()

  /**
   * Emits a value whenever a pending transaction is removed.
   */
  readonly pendingTransactionRemoved$ =
    this._pendingTransactionRemoved.asObservable()

  /**
   * Tracks pending transactions.
   */
  readonly pendingTransactions = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new PendingTransaction(),
  })

  /**
   * Tracks draft payouts.
   */
  readonly draftPayouts = collection<DraftPayout, string, DraftPayoutProps>({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new DraftPayout(),
  })

  constructor(
    private readonly _api: PayoutsAdminApi,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _listWorkspaces: ListWorkspacesFn,
    private readonly _searchMembers: SearchMembersFn,
  ) {}

  /**
   * Lists the platform workspaces that have payouts enabled.
   */
  listPayoutWorkspaces(query: ListWorkspacesQuery, signal?: AbortSignal) {
    signal?.throwIfAborted()
    return this._listAllPlatformWorkspaces().then((page) =>
      CursorPaged.of(
        filterBySearchText(page.data, query.searchText, (w) => w.name).filter(
          (w) => w.config.payouts?.enabled,
        ),
      ),
    )
  }

  /**
   * Lists the members of a workspace.
   */
  listWorkspaceMembers(query: ListWorkspaceMembersQuery, signal?: AbortSignal) {
    return this._searchMembers(
      {
        workspaceId: query.workspaceId,
        limit: query.limit,
        after: query.after,
        searchText: query.searchText,
      },
      signal,
    )
  }

  /**
   * Fetches the current draft payout.
   */
  async fetchCurrentDraftPayout(signal?: AbortSignal): Promise<DraftPayout> {
    // When there is no current draft, it is likely because one is being
    // created after having submitted one, so we can wait for it.
    const response = await NotFound.retry(
      () => this._api.draftPayouts.getCurrent(signal),
      { tries: 5, minTimeout: 200, maxTimeout: 1_000 },
    )
    return this.draftPayouts.set(this._mapDraftPayoutResourceToProps(response))
  }

  /**
   * Lists pending transactions.
   *
   * @param query
   * @param signal
   */
  async listPendingTransactions(
    query: ListPendingTransactionsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<PendingTransaction>> {
    const response = await this._api.pendingTransactions.list(
      {
        page_size: query.limit,
        page_token: query.after,
        order_by: 'date_created desc',
        recipient_user_id: query.recipientUserId,
        workspace_ids: query.workspaceIds?.length
          ? query.workspaceIds?.join(',')
          : undefined,
        date_transacted_from: query.dateTransacted?.from ?? undefined,
        date_transacted_to: query.dateTransacted?.to ?? undefined,
        date_created_from: query.dateCreated?.from ?? undefined,
        date_created_to: query.dateCreated?.to ?? undefined,
        search_text: query.searchText,
        flags: query.flags?.map(mapTransactionFlagToResource).join(','),
        transaction_source: query.sources
          ?.map(mapTransactionSourceTypeToResource)
          .join(','),
      },
      signal,
    )

    return {
      next: response.next_page_token,
      total: response.total_size,
      data: this.pendingTransactions.set(
        response.data.map((r) => this._mapPendingTransactionResourceToProps(r)),
      ),
    }
  }

  /**
   * Creates a new user transaction.
   *
   * @param command
   */
  async createUserTransaction(command: CreateUserTransaction): Promise<void> {
    // Create the transaction
    const { id } = await this._api.transactions.createUserTransaction({
      recipient_user_id: Id.assert(command.recipient.user.publicId),
      workspace_id: Id.assert(command.recipient.workspace.id),
      type: mapTransactionTypeToResource(command.type),
      reason: mapUserTransactionReasonToResource(command.reason),
      amount: command.amount,
      memo: command.memo,
    })

    // Retrieve the transaction in the background and add it to the
    // pending list.
    void this._retrieveCreatedTransaction(id).catch(noop)
  }

  /**
   * Cancels a user transaction.
   *
   * @param command
   */
  async cancelUserTransaction(command: CancelUserTransaction): Promise<void> {
    await this._api.transactions.cancelUserTransaction(
      Id.assert(command.transaction.id),
    )
    this.pendingTransactions.remove(command.transaction)
    this._pendingTransactionRemoved.next(command.transaction)
  }

  /**
   * Approves transactions for payout.
   */
  async approveTransactions(command: ApproveTransactions): Promise<void> {
    const { draft, transactions } = command
    const approvedBy = this._getCurrentUser()
    const approvedAt = new Date()
    const response = await this._api.draftPayouts.approveTransactions(
      Id.assert(draft.id),
      {
        transaction_ids: transactions.map((t) => Id.assert(t.id)),
      },
    )

    draft.approveTransactions(
      transactions.map((t) =>
        this._mapPendingTransactionToApprovedTransactionProps(
          t,
          approvedBy,
          approvedAt,
        ),
      ),
      response.etag,
    )
  }

  /**
   * Removes approved transactions from a draft payout.
   */
  async removeTransactions(command: RemoveTransactions): Promise<void> {
    const { draft, transactions } = command
    const response = await this._api.draftPayouts.removeTransactions(
      Id.assert(draft.id),
      {
        transaction_ids: transactions.map((t) => Id.assert(t.id)),
      },
    )

    draft.removeTransactions(transactions, response.etag)
  }

  /**
   * Submits the payout.
   */
  async submitPayout(command: SubmitPayout): Promise<void> {
    const { draft } = command
    await this._api.draftPayouts.submitPayout(Id.assert(draft.id), {
      etag: draft.etag,
    })
  }

  /**
   * Checks if the payout has completed by asking the server.
   */
  async fetchPayoutCompletion(id: Id): Promise<boolean> {
    // We know that if this gets called, then the payout does exist.
    const response = await NotFound.retry(
      () => this._api.payouts.get(Id.assert(id)),
      {
        tries: 5,
      },
    )
    return response.completed
  }

  /**
   * Retrieves a recently created transaction and pushes it to consumers.
   *
   * @param id
   * @private
   */
  private async _retrieveCreatedTransaction(id: string) {
    // Retrieve the transaction that was just created.
    // We do this in a retry since the read side might be eventually
    // consistent.
    const transaction = await NotFound.retry(
      () => this._api.transactions.get(id),
      { tries: 5 },
    )
    const pendingTransaction = this.pendingTransactions.set(
      this._mapPendingTransactionResourceToProps(transaction),
    )
    this._pendingTransactionAdded.next(pendingTransaction)
  }

  // The Workspace Find API doesn't paginate correctly, so we query for all
  // workspaces once and memoize the result. A failed query is not memoized,
  // so that the next call retries.
  private _listAllPlatformWorkspaces() {
    this._allPlatformWorkspaces ??= this._listWorkspaces({
      after: null,
      limit: 10_000,
    }).catch((err: unknown) => {
      this._allPlatformWorkspaces = null
      throw err
    })
    return this._allPlatformWorkspaces
  }

  private _mapPendingTransactionResourceToProps(
    resource: PendingTransactionResource,
  ): Partial<PendingTransactionProps> {
    return {
      id: resource.id,
      shortId: resource.short_id,
      amount: resource.amount,
      createdAt: maybeParseDate(resource.date_created),
      transactedAt: maybeParseDate(resource.date_transacted),
      iteration: resource.iteration,
      recipient: resource.recipient_user_id
        ? this._primeUser({ publicId: resource.recipient_user_id })
        : undefined,
      workspace: resource.workspace_id
        ? this._primeWorkspace({ id: resource.workspace_id })
        : undefined,
      type: mapTransactionTypeResourceToTransactionType(resource.type),
      source: mapTransactionSourceTypeResourceToTransactionSourceType(
        resource.source,
      ),
      reason:
        resource.reason &&
        mapUserTransactionReasonResourceToUserTransactionReason(
          resource.reason,
        ),
      memo: resource.memo,
      jobId: Id(resource.job_id),
      flags: resource.flags.map(mapTransactionFlagResourceToTransactionFlag),
      lastFailureReason:
        resource.last_failure_reason &&
        mapTransactionFailureReasonResourceToTransactionFailureReason(
          resource.last_failure_reason,
        ),
      lastFailureDescription: resource.last_failure_description,
    }
  }

  private _mapPendingTransactionToApprovedTransactionProps(
    transaction: PendingTransaction,
    approvedBy: User,
    approvedAt: Date,
  ): Partial<ApprovedTransactionProps> {
    return {
      id: transaction.id,
      approvedBy,
      approvedAt,
      createdAt: transaction.createdAt,
      transactedAt: transaction.transactedAt,
      shortId: transaction.shortId,
      amount: transaction.amount,
      type: transaction.type,
      recipient: transaction.recipient,
      workspace: transaction.workspace,
      source: transaction.source,
      reason: transaction.reason,
      jobId: transaction.jobId,
      flags: transaction.flags.slice(),
    }
  }

  private _mapDraftPayoutResourceToProps(
    resource: DraftPayoutResource,
  ): Partial<DraftPayoutProps> {
    return {
      id: resource.id,
      etag: resource.etag,
      totalAmount: resource.total_amount,
      approvedTransactions: resource.approved_transactions.map((t) =>
        this._mapApprovedTransactionResourceToProps(t),
      ),
    }
  }

  private _mapApprovedTransactionResourceToProps(
    resource: ApprovedTransactionResource,
  ): Partial<ApprovedTransactionProps> {
    return {
      id: resource.id,
      shortId: resource.short_id,
      amount: resource.amount,
      createdAt: maybeParseDate(resource.date_created),
      transactedAt: maybeParseDate(resource.date_transacted),
      approvedAt: maybeParseDate(resource.date_approved),
      approvedBy: resource.approved_by_user_id
        ? this._primeUser({ publicId: resource.approved_by_user_id })
        : undefined,
      recipient: resource.recipient_user_id
        ? this._primeUser({ publicId: resource.recipient_user_id })
        : undefined,
      workspace: resource.workspace_id
        ? this._primeWorkspace({ id: resource.workspace_id })
        : undefined,
      type: mapTransactionTypeResourceToTransactionType(resource.type),
      source: mapTransactionSourceTypeResourceToTransactionSourceType(
        resource.source,
      ),
      reason:
        resource.reason &&
        mapUserTransactionReasonResourceToUserTransactionReason(
          resource.reason,
        ),
      jobId: Id(resource.job_id),
      flags: resource.flags.map(mapTransactionFlagResourceToTransactionFlag),
    }
  }
}

/**
 * Lists pending transactions.
 */
export interface ListPendingTransactionsQuery extends CursorPageQuery {
  recipientUserId?: Id
  dateCreated?: TransactionDateRange | null
  dateTransacted?: TransactionDateRange | null
  flags?: TransactionFlag[] | null
  sources?: TransactionSourceType[] | null
  workspaceIds?: Id[] | null
  searchText?: string
}

/**
 * Command for creating a user transaction
 */
export interface CreateUserTransaction {
  /**
   * The workspace member to create the transaction for.
   */
  recipient: Member
  /**
   * The transaction memo.
   */
  memo: string
  /**
   * The transaction amount.
   */
  amount: number
  /**
   * The transaction type.
   */
  type: TransactionType
  /**
   * The reason for the transaction.
   */
  reason: UserTransactionReason
}

/**
 * Command for cancelling a user transaction.
 */
export interface CancelUserTransaction {
  /**
   * The transaction being canceled.
   */
  transaction: PendingTransaction
}

/**
 * Command for approving transactions for payout.
 */
export interface ApproveTransactions {
  draft: DraftPayout
  transactions: PendingTransaction[]
}

/**
 * Command for removing approved transactions from a draft payout.
 */
export interface RemoveTransactions {
  draft: DraftPayout
  transactions: ApprovedTransaction[]
}

/**
 * Command for submitting a draft payout.
 */
export interface SubmitPayout {
  draft: DraftPayout
}

/**
 * Query for workspaces.
 */
export interface ListWorkspacesQuery extends Partial<CursorPageQuery> {
  searchText?: string
}

/**
 * Query for workspace members.
 */
export interface ListWorkspaceMembersQuery extends Partial<CursorPageQuery> {
  searchText?: string
  workspaceId: Id
}

function filterBySearchText<T>(
  items: T[],
  searchText: string | undefined,
  selector: (item: T) => string,
): T[] {
  const lowerSearchText = searchText?.toLowerCase()
  if (!lowerSearchText) {
    return items
  }
  return items.filter((item) =>
    selector(item).toLowerCase().includes(lowerSearchText),
  )
}
